import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { jobsEndpoint } from "../constants";
import CVLibraryLogo from "../assets/cvl-og-image.jpg";


// Add this helper function at the top of your component or in a utils file
const decodeBase64Html = (encodedHtml) => {
  try {
    // Check if the content looks like Base64 (long string without HTML tags)
    if (encodedHtml && !encodedHtml.includes('<') && encodedHtml.length > 100) {
      // Decode Base64 to HTML
      return atob(encodedHtml);
    }
    // If it's already HTML, return as-is
    return encodedHtml;
  } catch (error) {
    console.error('Error decoding Base64 HTML:', error);
    return encodedHtml; // Return original if decoding fails
  }
};

// Helper function to download base64 content as file
const downloadBase64File = (base64Content, filename, contentType) => {
  try {
    // Convert base64 to blob
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: contentType });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading file:', error);
    alert('Failed to download CV file');
  }
};

const CVDetails = () => {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registerUrl, setRegisterUrl] = useState(null);
  const [hasClientAccess, setHasClientAccess] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [isFullView, setIsFullView] = useState(false);
  const [loadingFullView, setLoadingFullView] = useState(false);
  const [loadingCvDownload, setLoadingCvDownload] = useState(false);
  const [credits, setCredits] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  
  // New state for save candidate functionality
  const [isSavingCandidate, setIsSavingCandidate] = useState(false);
  const [candidateSaved, setCandidateSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [lastDownloadedCv, setLastDownloadedCv] = useState(null);
  
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchCandidateDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // First, get partial view to check access level
        const response = await axios.post(
          jobsEndpoint + 'cv-library/candidate/partial/',
          { record_id: recordId, html_preview: 1 },
          {
            headers: {
              'Authorization': `Token ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          setCandidate(response.data.candidate);
          setRegisterUrl(response.data.candidate.register_url);
          setHasClientAccess(response.data.has_client_access);
          setSessionId(response.data.session_id || '');
          
          // If user has client access, automatically fetch full view and credits
          if (response.data.has_client_access) {
            await fetchFullView(response.data.session_id || '');
            await fetchCredits();
          }
        } else {
          setError("Failed to load candidate details");
        }
      } catch (err) {
        console.error("Failed to get candidate details:", err);
        setError("Failed to load candidate details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (recordId) {
      fetchCandidateDetails();
    }
  }, [recordId, token]);

  const fetchFullView = async (currentSessionId = sessionId) => {
  if (!hasClientAccess) {
    console.log("No client access - cannot fetch full view");
    return;
  }

  try {
    setLoadingFullView(true);
    
    const response = await axios.post(
      jobsEndpoint + 'cv-library/candidate/full/',
      { 
        record_id: recordId, 
        session_id: currentSessionId,
        html_preview: 1,
        encode: 1
      },
      {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.data.success) {
      const fullCandidateData = response.data.candidate_data || response.data.candidate;
      
      // Handle CV content with proper decoding
      const curriculumVitae = fullCandidateData.Curriculum_Vitae || 
                             fullCandidateData.curriculum_vitae ||
                             fullCandidateData.CV ||
                             fullCandidateData.cv;
      
      const decodedCV = curriculumVitae ? decodeBase64Html(curriculumVitae) : null;
      
      // Create the updated candidate object with all full view data
      const updatedCandidate = {
        // Keep existing partial view data as fallback
        ...candidate,
        // Override with full view data (this will include all the unlocked attributes)
        ...fullCandidateData,
        // Ensure the decoded CV is properly set with all possible field names
        curriculum_vitae: decodedCV,
        Curriculum_Vitae: decodedCV,
        // Preserve the name field if it exists in either dataset
        name: fullCandidateData.name || fullCandidateData.Name || candidate.name || candidate.Name,
        Name: fullCandidateData.Name || fullCandidateData.name || candidate.Name || candidate.name
      };
      
      console.log("Full candidate data received:", fullCandidateData);
      console.log("Updated candidate object:", updatedCandidate);
      
      // Update the candidate state with the merged data
      setCandidate(updatedCandidate);
      setSessionId(response.data.session_id || currentSessionId);
      setIsFullView(true);
      
      console.log("Successfully loaded full candidate view with all attributes");
    } else {
      console.error("Failed to load full candidate view:", response.data.error);
      // Optionally show user-friendly error message
      setError("Failed to unlock full candidate view. Please try again.");
    }
  } catch (err) {
    console.error("Failed to get full candidate details:", err);
    // Don't set error here as we still have partial view, but log the issue
    console.warn("Full view fetch failed, continuing with partial view");
  } finally {
    setLoadingFullView(false);
  }
};

  const fetchCredits = async () => {
    if (!hasClientAccess) {
      return;
    }

    try {
      setLoadingCredits(true);
      
      const response = await axios.post(
        jobsEndpoint + 'cv-library/credits/',
        {},
        {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setCredits(response.data.credits_remaining);
        setSessionId(response.data.session_id || sessionId);
      }
    } catch (err) {
      console.error("Failed to fetch credits:", err);
    } finally {
      setLoadingCredits(false);
    }
  };

  const downloadCandidateCV = async () => {
    if (!hasClientAccess) {
      alert("CV-Library client access required for CV document download");
      return;
    }

    try {
      setLoadingCvDownload(true);
      
      const response = await axios.post(
        jobsEndpoint + 'cv-library/download-cv/',
        { record_id: recordId },
        {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        // Update session ID
        setSessionId(response.data.session_id || sessionId);
        
        // Store the CV data for potential saving
        setLastDownloadedCv({
          base64_content: response.data.base64_content,
          filename: response.data.filename,
          content_type: response.data.content_type
        });
        
        // Download the file
        downloadBase64File(
          response.data.base64_content,
          response.data.filename,
          response.data.content_type
        );
        
        // Refresh credits after download
        await fetchCredits();
        
        alert(`CV downloaded successfully: ${response.data.filename}`);
      } else {
        if (response.data.session_conflict) {
          alert("Session conflict detected. Please refresh the page and try again.");
        } else {
          alert(`Failed to download CV: ${response.data.error}`);
        }
      }
    } catch (err) {
      console.error("Failed to download CV:", err);
      if (err.response?.status === 403) {
        alert("You don't have permission to download CVs. CV-Library client access is required.");
      } else {
        alert("Failed to download CV. Please try again.");
      }
    } finally {
      setLoadingCvDownload(false);
    }
  };

  // Enhanced function to upload CV file from base64 data with better filename preservation
const uploadCvFromBase64 = async (base64Content, filename, contentType) => {
  try {
    // Convert base64 to file
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    
    // Ensure the filename has proper extension
    let finalFilename = filename;
    if (!finalFilename.toLowerCase().endsWith('.docx') && 
        !finalFilename.toLowerCase().endsWith('.doc') && 
        !finalFilename.toLowerCase().endsWith('.pdf')) {
      // Add .docx extension if missing (assuming most CVs are Word docs)
      finalFilename += '.docx';
    }
    
    const file = new File([byteArray], finalFilename, { 
      type: contentType,
      lastModified: Date.now()
    });

    // Create FormData and upload
    const formData = new FormData();
    formData.append('file_0', file);
    formData.append('file_type', 'cv');
    // Explicitly send the original filename
    formData.append('original_filename', finalFilename);
    
    console.log('Uploading file:', {
      name: file.name,
      size: file.size,
      type: file.type,
      originalFilename: finalFilename
    });

    const response = await fetch(jobsEndpoint + 'upload_files/', {
      method: 'POST',
      headers: {
        'Authorization': `Token ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to upload CV: ${errorText}`);
    }

    const result = await response.json();
    console.log('Upload result:', result);
    return result.file_ids || [];
  } catch (error) {
    console.error('Error uploading CV:', error);
    throw error;
  }
};

// Alternative decoding function that handles different encodings
const decodeHtmlContent = (content) => {
  if (!content) return '';
  
  try {
    // First, check if it's Base64 encoded
    if (!content.includes('<') && content.length > 100) {
      // Try Base64 decoding with UTF-8 handling
      const binaryString = atob(content);
      
      // Check if the content contains non-ASCII characters that suggest encoding issues
      const hasEncodingIssues = /[\u00C0-\u00FF]/.test(binaryString);
      
      if (hasEncodingIssues) {
        // Handle as Latin-1 (ISO-8859-1) encoded content
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        // Decode as UTF-8
        const decoder = new TextDecoder('utf-8', { fatal: false });
        const decoded = decoder.decode(bytes);
        
        // If UTF-8 decoding produces replacement characters, try Windows-1252
        if (decoded.includes('\uFFFD')) {
          const decoder1252 = new TextDecoder('windows-1252');
          return decoder1252.decode(bytes);
        }
        
        return decoded;
      }
      
      return binaryString;
    }
    
    // If it's already HTML, clean up any encoding issues
    return content
      .replace(/â€™/g, "'")  // Smart apostrophe
      .replace(/â€œ/g, '"')  // Smart quote left
      .replace(/â€/g, '"')   // Smart quote right
      .replace(/â€"/g, '—')  // Em dash
      .replace(/Â/g, '')     // Remove stray Â characters
      .replace(/â€¢/g, '•')  // Bullet point
      .replace(/Ã©/g, 'é')   // e with acute accent
      .replace(/Ã¡/g, 'á')   // a with acute accent
      .replace(/Ã­/g, 'í')   // i with acute accent
      .replace(/Ã³/g, 'ó')   // o with acute accent
      .replace(/Ãº/g, 'ú');  // u with acute accent
      
  } catch (error) {
    console.error('Error decoding HTML content:', error);
    return content;
  }
};

// Enhanced sanitization function
const sanitizeHtmlContent = (html) => {
  if (!html) return '';
  
  // First decode the content properly
  const decodedHtml = decodeHtmlContent(html);
  
  // Create a temporary DOM element to parse and clean the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = decodedHtml;
  
  // Remove any script tags for security
  const scripts = tempDiv.querySelectorAll('script');
  scripts.forEach(script => script.remove());
  
  // Fix common encoding issues in text nodes
  const walker = document.createTreeWalker(
    tempDiv,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );
  
  const textNodes = [];
  let node;
  while (node = walker.nextNode()) {
    textNodes.push(node);
  }
  
  textNodes.forEach(textNode => {
    textNode.textContent = textNode.textContent
      .replace(/â€™/g, "'")
      .replace(/â€œ/g, '"')
      .replace(/â€/g, '"')
      .replace(/â€"/g, '—')
      .replace(/Â/g, '')
      .replace(/â€¢/g, '•')
      .replace(/Ã©/g, 'é')
      .replace(/Ã¡/g, 'á')
      .replace(/Ã­/g, 'í')
      .replace(/Ã³/g, 'ó')
      .replace(/Ãº/g, 'ú');
  });
  
  return tempDiv.innerHTML;
};
  // New function to save candidate to the system
  const saveCandidateToSystem = async () => {
    if (!lastDownloadedCv) {
      alert('Please download the CV first before saving the candidate.');
      return;
    }

    if (candidateSaved) {
      alert('This candidate has already been saved to your system.');
      return;
    }

    try {
      setIsSavingCandidate(true);
      setSaveError(null);

      // Upload the CV file
      const cvFileIds = await uploadCvFromBase64(
        lastDownloadedCv.base64_content,
        lastDownloadedCv.filename,
        lastDownloadedCv.content_type
      );

      if (cvFileIds.length === 0) {
        throw new Error('Failed to upload CV file');
      }

      // Create the candidate profile
      const response = await fetch(jobsEndpoint + 'cvlib_create/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({
          cv_file_ids: cvFileIds
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to create candidate profile');
      }

      const result = await response.json();
      setCandidateSaved(true);
      alert('Candidate has been successfully saved to your system!');
      
      // Optionally refresh credits as the candidate has been saved
      await fetchCredits();

    } catch (error) {
      console.error('Failed to save candidate:', error);
      setSaveError(error.message || 'Failed to save candidate');
      alert(`Failed to save candidate: ${error.message}`);
    } finally {
      setIsSavingCandidate(false);
    }
  };

  const handleUnlockCandidate = () => {
    if (hasClientAccess && !isFullView) {
      // User has access but hasn't loaded full view yet
      fetchFullView();
    } else if (registerUrl) {
      // User needs to register with CV Library
      window.open(registerUrl, '_blank');
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="lg:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#19bfb7] mx-auto mb-4"></div>
            <p className="text-gray-500">Loading candidate details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lg:p-8">
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.98-.833-2.75 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Candidate</h3>
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={handleGoBack}
              className="px-4 py-2 text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6]"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="lg:p-8">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-gray-500">Candidate not found</p>
          <button
            onClick={handleGoBack}
            className="mt-4 px-4 py-2 text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6]"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Search
          </button>
        </div>
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-semibold text-gray-900">
                {candidate.name || candidate.Name || 'Candidate Profile'}
              </h1>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">from</span>
                <img 
                  src={CVLibraryLogo} 
                  alt="CV Library" 
                  className="h-6 w-auto object-contain"
                />
              </div>
            </div>
            <p className="mt-1 text-lg text-gray-600">
              {candidate.current_job_title || candidate.Current_job_title}
            </p>
            <p className="text-sm text-gray-500">
              {candidate.town || candidate.Town}
              {(candidate.county || candidate.County) && `, ${candidate.county || candidate.County}`}
            </p>
            
            {/* Access Status Indicator */}
            {hasClientAccess && (
              <div className="mt-2 flex items-center gap-3">
                {isFullView ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Full Access - Complete Profile
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    CV Library Client - Full Access Available
                  </span>
                )}
                
                {/* Saved Status Indicator */}
                {candidateSaved && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Saved to System
                  </span>
                )}
                
                {/* Credits Display */}
                {credits !== null && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" />
                    </svg>
                    {credits} Credits Remaining
                  </span>
                )}
                
                {loadingCredits && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                )}
              </div>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            {!hasClientAccess ? (
              <button
                onClick={handleUnlockCandidate}
                className="px-4 py-2 text-[#19bfb7] border border-[#19bfb7] rounded-lg hover:bg-[#19bfb7] hover:text-white transition-colors"
              >
                Sign up with CV Library to unlock full candidates
              </button>
            ) : !isFullView ? (
              <button
                onClick={handleUnlockCandidate}
                disabled={loadingFullView}
                className="px-4 py-2 text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loadingFullView ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                    </svg>
                    View Full Profile
                  </>
                )}
              </button>
            ) : (
              <>
                <div className="flex items-center gap-2 text-green-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Full Profile Unlocked
                </div>
                
                {/* Conditional Download/Save Buttons */}
                {!candidateSaved ? (
                  <>
                    {/* Download CV Button */}
                    <button
                      onClick={downloadCandidateCV}
                      disabled={loadingCvDownload}
                      className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {loadingCvDownload ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Downloading...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download CV
                        </>
                      )}
                    </button>

                    {/* Save Candidate Button - only show if CV has been downloaded */}
                    {lastDownloadedCv && (
                      <button
                        onClick={saveCandidateToSystem}
                        disabled={isSavingCandidate}
                        className="px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isSavingCandidate ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            Save to System
                          </>
                        )}
                      </button>
                    )}
                  </>
                ) : (
                  <div className="px-4 py-2 text-purple-700 bg-purple-100 rounded-lg border border-purple-200 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Candidate Saved Successfully
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Professional Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Position</label>
                  <p className="text-gray-900">
                    {candidate.current_job_title || candidate.Current_job_title || 'Not specified'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Desired Position</label>
                  <p className="text-gray-900">
                    {candidate.desired_job_title || candidate.Desired_job_title || 'Not specified'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Industry</label>
                  <p className="text-gray-900">
                    {candidate.preferred_industry || candidate.Preferred_Industry || 'Not specified'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employment Term</label>
                  <p className="text-gray-900">
                    {candidate.employment_term || candidate.Employment_term || 'Not specified'}
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected Salary</label>
                  <p className="text-gray-900">
                    {candidate.expected_salary || candidate.Expected_salary || 'Not specified'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Available</label>
                  <p className="text-gray-900">
                    {candidate.date_available || candidate.Date_available || 'Not specified'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">UK Driving Licence</label>
                  <p className="text-gray-900">
                    {candidate.uk_driving_licence || candidate.UK_driving_licence || 'Not specified'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Willing to Travel</label>
                  <p className="text-gray-900">
                    {candidate.travel || candidate.Travel || 'Not specified'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Skills */}
          {(candidate.main_skills || candidate.Main_Skills) && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Key Skills</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700">{candidate.main_skills || candidate.Main_Skills}</p>
              </div>
            </div>
          )}

          {/* CV Preview */}
          {(candidate.curriculum_vitae || candidate.Curriculum_Vitae) && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">CV Preview</h2>
                
                {/* Download/Save CV buttons in preview section */}
                {hasClientAccess && isFullView && !candidateSaved && (
                  <div className="flex gap-2">
                    <button
                      onClick={downloadCandidateCV}
                      disabled={loadingCvDownload}
                      className="px-3 py-1.5 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      {loadingCvDownload ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          Downloading...
                        </>
                      ) : (
                        <>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download
                        </>
                      )}
                    </button>
                    
                    {lastDownloadedCv && (
                      <button
                        onClick={saveCandidateToSystem}
                        disabled={isSavingCandidate}
                        className="px-3 py-1.5 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {isSavingCandidate ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            Save
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
                
                {candidateSaved && (
                  <div className="px-3 py-1.5 text-sm text-purple-700 bg-purple-100 rounded-lg border border-purple-200 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Saved to System
                  </div>
                )}
              </div>
              
             <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
      <div 
        className="prose max-w-none text-gray-700"
        dangerouslySetInnerHTML={{ 
          __html: sanitizeHtmlContent(candidate.curriculum_vitae || candidate.Curriculum_Vitae)
        }}
      />
    </div>
              
              {/* Show appropriate notice based on access level */}
              {!isFullView && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-yellow-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h3 className="text-sm font-medium text-yellow-800">
                        {hasClientAccess ? 'Partial Preview' : 'Limited Preview'}
                      </h3>
                      <p className="text-sm text-yellow-700 mt-1">
                        {hasClientAccess 
                          ? 'This is a redacted preview. Click "View Full Profile" above to see complete contact details and CV.'
                          : 'This is a redacted preview. Contact details are hidden. Sign up with CV-Library for full access.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {isFullView && !candidateSaved && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h3 className="text-sm font-medium text-green-800">Full Profile Access</h3>
                      <p className="text-sm text-green-700 mt-1">
                        You have access to the complete candidate profile. Download the CV and save the candidate to your system for future reference.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {candidateSaved && (
                <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-purple-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h3 className="text-sm font-medium text-purple-800">Candidate Saved Successfully</h3>
                      <p className="text-sm text-purple-700 mt-1">
                        This candidate has been saved to your system and is now available in your candidate database.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Willing to Relocate</label>
                <p className="text-gray-900">
                  {candidate.willing_to_relocate || candidate.Willing_to_relocate || 'Not specified'}
                </p>
              </div>
            </div>
          </div>

          {/* Credits and Account Info */}
          {hasClientAccess && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Information</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Credits Remaining</label>
                  <div className="flex items-center gap-2">
                    {loadingCredits ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#19bfb7]"></div>
                    ) : credits !== null ? (
                      <span className="text-2xl font-bold text-[#19bfb7]">{credits}</span>
                    ) : (
                      <span className="text-gray-500">--</span>
                    )}
                    <button
                      onClick={fetchCredits}
                      disabled={loadingCredits}
                      className="text-sm text-[#19bfb7] hover:text-[#18ada6] disabled:opacity-50"
                    >
                      Refresh
                    </button>
                  </div>
                </div>
                {sessionId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Session ID</label>
                    <p className="text-xs text-gray-500 font-mono break-all">{sessionId}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Save Status */}
          {hasClientAccess && isFullView && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Save to System</h2>
              {candidateSaved ? (
                <div className="text-center p-4">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 mb-3">
                    <svg className="h-6 w-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">Candidate Saved</h3>
                  <p className="text-sm text-gray-500">This candidate is now in your system</p>
                </div>
              ) : lastDownloadedCv ? (
                <div className="space-y-3">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">
                      <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      CV Downloaded - Ready to Save
                    </p>
                  </div>
                  <button
                    onClick={saveCandidateToSystem}
                    disabled={isSavingCandidate}
                    className="w-full px-4 py-3 text-white bg-purple-600 border border-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSavingCandidate ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving Candidate...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Save Candidate to System
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="text-center p-4">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-3">
                    <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">Download CV First</h3>
                  <p className="text-sm text-gray-500">Download the CV to enable saving to your system</p>
                </div>
              )}
            </div>
          )}

          {/* Profile Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Profile Details</h2>
            <div className="space-y-3">
              {(candidate.quickview_ref || candidate.Quickview_Ref) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                  <p className="text-gray-900 font-mono text-sm">
                    {candidate.quickview_ref || candidate.Quickview_Ref}
                  </p>
                </div>
              )}
              {(candidate.date_submitted || candidate.Date_Submitted) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Submitted</label>
                  <p className="text-gray-900">{candidate.date_submitted || candidate.Date_Submitted}</p>
                </div>
              )}
              {(candidate.last_modified || candidate.Last_Modified) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Modified</label>
                  <p className="text-gray-900">{candidate.last_modified || candidate.Last_Modified}</p>
                </div>
              )}
              {(candidate.source || candidate.Source) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <p className="text-gray-900">{candidate.source || candidate.Source}</p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Actions</h2>
            <div className="space-y-3">
              {hasClientAccess && isFullView && !candidateSaved && (
                <button
                  onClick={downloadCandidateCV}
                  disabled={loadingCvDownload}
                  className="w-full px-4 py-3 text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loadingCvDownload ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Downloading CV...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Original CV
                    </>
                  )}
                </button>
              )}
              <button
                onClick={() => window.print()}
                className="w-full px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Print CV Preview
              </button>
              <button
                onClick={handleGoBack}
                className="w-full px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back to Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CVDetails;